import { AddCircleOutlineOutlined, Refresh } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CardMedia,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import emptyProvisionImg from "../../assets/empty-provision-img.png";
import TableLoader from "../../components/loader/tableLoader";
import { setGlobalLoader } from "../../redux/actions/commonAction";
import { getAllCredentials } from "../../redux/actions/credentialAction";
import {
  approveAndApply,
  cancelTerraformPlan,
  getAllProvision,
  getAllServices,
  getAnalyticsByProvision,
  getLogsByProvisionId,
  terminateDestroy,
  terminatePlan,
} from "../../redux/actions/provisionAction";
import { getCredentialsBasedOnRoles, isAdmin } from "../../utils/helpers";
import ProvisionFilter from "./components/filter";
import ProvisionTable from "./components/provisionTable";
import Types from "../../redux/types";

const Provision = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { allCredentials } = useSelector((state) => state.credential);
  const { allProvisions, allCostAndSecurity } = useSelector(
    (state) => state.provision
  );
  const loggedUser = useSelector((state) => state?.users?.loggedUser?.result);
  const filteredCredentials = getCredentialsBasedOnRoles(
    allCredentials,
    loggedUser
  );
  const [filteredProvisions, setFilteredProvisions] = useState([]);
  const [filters, setFilters] = useState({
    createdBy: "all",
    services: [],
    accounts: [],
  });
  const [fetchingData, setFetchingData] = useState(true);
  const serviceList = [
    ...new Set(allProvisions?.map((provision) => provision?.service)),
  ];

  useEffect(() => {
    fetchData();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (allCredentials?.length > 0 && allProvisions.length > 0) {
      //change the logic to all provisions
      dispatch(setGlobalLoader(false));
    }
    //eslint-disable-next-line
  }, [allCredentials, allProvisions]);

  useEffect(() => {
    const { createdBy, services, accounts } = filters;
    const selectedAccNumbers = accounts?.map((acc) => acc.accountNumber);
    const userEmail = loggedUser?.email;

    const filteredProv = allProvisions?.filter(
      (prov) =>
        (selectedAccNumbers.length === 0
          ? true
          : selectedAccNumbers?.includes(prov?.accountNumber)) &&
        (createdBy === "all" ? true : prov?.createdBy === userEmail) &&
        (services.length === 0 ? true : services?.includes(prov?.service))
    );
    setFilteredProvisions(filteredProv);
    //eslint-disable-next-line
  }, [filters, allProvisions]);

  const fetchData = async () => {
    dispatch(getAllCredentials());
    dispatch(getAllServices());
    dispatch(setGlobalLoader(true));
    let done = await dispatch(getAllProvision());
    if (done) {
      setFetchingData(false);
    }
  };

  const onApply = (data) => {
    clearCostAndSecurity(data?.id);
    const payload = {
      path: data?.filePath,
      credentials: data?.credentials,
      region: data?.variables?.region,
      provisionId: data?.id,
    };
    dispatch(approveAndApply(payload));
    setTimeout(() => {
      dispatch(getAllProvision());
    }, 1000);
  };

  const onReject = (data) => {
    clearCostAndSecurity(data?.id);
    const payload = {
      provisionId: data?.id,
    };
    dispatch(cancelTerraformPlan(payload));
    setTimeout(() => {
      dispatch(getAllProvision());
    }, 1000);
  };

  const onTerminate = (data, action) => {
    clearCostAndSecurity(data?.id);
    const payload = {
      path: data?.filePath,
      credentials: data?.credentials,
      region: data?.variables?.region,
      provisionId: data?.id,
    };
    if (action === "terminate") {
      dispatch(terminatePlan(payload));
    } else {
      dispatch(terminateDestroy(payload));
    }
    setTimeout(() => {
      dispatch(getAllProvision());
    }, 1000);
  };

  const onFilterChange = (updatedfilter) => {
    setFilters(updatedfilter);
  };

  const getLogsById = async (provisionId) => {
    const updatedLogs = await dispatch(getLogsByProvisionId(provisionId));
    return updatedLogs?.[provisionId];
  };

  const clearCostAndSecurity = (provisionId) => {
    const updatedCostAndSecurity = { ...allCostAndSecurity };
    delete updatedCostAndSecurity[provisionId];
    dispatch({
      type: Types.PROVISION_COST_AND_SECURITY,
      payload: updatedCostAndSecurity,
    });
  };

  const getProvisionOverviewData = async (row, type) => {
    const label = type === "cost" ? "costByService" : "vulnDetail";
    if (allCostAndSecurity?.[row?.id]) {
      const latestStageData = allCostAndSecurity?.[
        row?.id
      ]?.costAndSecurity.find((item) => item.stage === row?.latestStage);
      return latestStageData?.[label];
    }
    dispatch(setGlobalLoader(true));
    const analyticsOverview = await dispatch(getAnalyticsByProvision(row?.id));
    dispatch(setGlobalLoader(false));
    return analyticsOverview?.costAndSecurity.find(
      (item) => item.stage === row?.latestStage
    )?.[label];
  };

  const renderEmptyScreen = () => {
    return fetchingData ? (
      <TableLoader />
    ) : (
      <Box
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"center"}
        alignItems={"center"}
        mt={10}
      >
        <CardMedia
          component="img"
          alt="My Image"
          image={emptyProvisionImg}
          sx={{
            maxWidth: "380px",
            width: "auto",
          }}
        />
        <Typography mt={2} mb={2}>
          Hello! Click the button to create a new provision
        </Typography>
        <Button
          startIcon={<AddCircleOutlineOutlined />}
          variant="contained"
          onClick={() => history.push("provision/new")}
        >
          Create Provision
        </Button>
      </Box>
    );
  };

  return (
    <Box>
      {allProvisions?.length > 0 ? (
        <>
          <Typography variant="h5" mb={3} gutterBottom={true}>
            Self-Service Provisioning
          </Typography>
          <Box
            mb={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              <Autocomplete
                size="small"
                multiple
                options={filteredCredentials}
                value={filters?.accounts}
                sx={{ minWidth: 300 }}
                onChange={(e, value) => {
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    accounts: value,
                  }));
                }}
                getOptionLabel={(option) => option.accountNumber}
                renderInput={(params) => (
                  <TextField
                    sx={{
                      ".MuiAutocomplete-input": { padding: "4px !important" },
                    }}
                    {...params}
                    margin="none"
                    label={"Accounts"}
                  />
                )}
              />
              <ProvisionFilter
                filtersEnabled={filters}
                onChange={onFilterChange}
                services={serviceList}
              />
            </Box>

            <Box display="flex" sx={{ gridGap: "12px" }}>
              <Button
                variant="outlined"
                onClick={() => fetchData()}
                startIcon={<Refresh />}
              >
                Refresh
              </Button>
              <Button
                disabled={!isAdmin(loggedUser?.roles)}
                startIcon={<AddCircleOutlineOutlined />}
                variant="contained"
                onClick={() => history.push("provision/new")}
              >
                Create New Provision
              </Button>
            </Box>
          </Box>
          <Box mt={2.25}>
            <ProvisionTable
              data={filteredProvisions}
              onApply={onApply}
              onReject={onReject}
              onTerminate={onTerminate}
              isAdmin={isAdmin(loggedUser?.roles)}
              getLogsById={getLogsById}
              getProvisionOverviewData={getProvisionOverviewData}
            />
          </Box>
        </>
      ) : (
        renderEmptyScreen()
      )}
    </Box>
  );
};

export default Provision;
